import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useTrainingCourse from "../../Hooks/useTrainingCourse";
import SubmitLoader from "../../Components/SubmitLoader";
import AlertMessage from "../../Components/AlertMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEye, faBookOpen, faUsers, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import noImageAvailable from "../../Assets/no-image-available.png";
import "./TrainingCourses.css";

const TrainingCourses = () => {
  const navigate = useNavigate();
  const {
    courses,
    userEnrollments,
    coursesLoading,
    enrollmentsLoading,
    coursesError,
    enrollmentsError,
    fetchCourses,
    fetchUserEnrollments,
    isEnrolledInCourse,
    getCourseEnrollment
  } = useTrainingCourse();

  // Utility function to get course cover image
  const getCourseCoverImage = (course) => {
    if (course.coverImageContent && course.coverImageExtension) {
      try {
        let base64String;

        // Handle different formats from .NET
        if (typeof course.coverImageContent === 'string') {
          // If it's already a base64 string, use it directly
          base64String = course.coverImageContent;
        } else if (course.coverImageContent instanceof Uint8Array) {
          // Convert Uint8Array to base64
          base64String = btoa(String.fromCharCode(...course.coverImageContent));
        } else if (Array.isArray(course.coverImageContent)) {
          // Convert regular array to base64
          base64String = btoa(String.fromCharCode(...new Uint8Array(course.coverImageContent)));
        } else {
          // console.warn('Unexpected coverImageContent format:', typeof course.coverImageContent);
          return { src: noImageAvailable, isPlaceholder: true };
        }

        // Handle extension format - remove leading dot if present
        const extension = course.coverImageExtension.startsWith('.') 
          ? course.coverImageExtension.substring(1) 
          : course.coverImageExtension;

        return { src: `data:image/${extension};base64,${base64String}`, isPlaceholder: false };
      } catch (error) {
        // console.error('Error converting cover image:', error);
        return { src: noImageAvailable, isPlaceholder: true };
      }
    }
    return { src: noImageAvailable, isPlaceholder: true };
  };

  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("enrolled"); // all, enrolled, available

  useEffect(() => {
    fetchCourses();
    fetchUserEnrollments();
  }, []);

  useEffect(() => {
    if (courses) {
      let filtered = courses;

      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(course =>
          course.courseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.courseCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.courseDecription?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Apply publish_status and activation_status filters with version management
      filtered = filterCoursesByStatusAndVersion(filtered);

      // Apply status filter
      if (statusFilter === "enrolled") {
        filtered = filtered.filter(course => isEnrolledInCourse(course.courseId));
      } 
      else if (statusFilter === "available") {
        filtered = filtered.filter(course => !isEnrolledInCourse(course.courseId));
      }

      setFilteredCourses(filtered);
    }
  }, [courses, userEnrollments, searchTerm, statusFilter]);

  // Course filtering logic for publish_status, activation_status, and version management
  const filterCoursesByStatusAndVersion = (coursesToFilter) => {
    // Group all courses by course code first (including inactive versions)
    const coursesByCode = {};
    coursesToFilter.forEach(course => {
      const courseCode = course.courseCode;
      if (!coursesByCode[courseCode]) {
        coursesByCode[courseCode] = [];
      }
      coursesByCode[courseCode].push(course);
    });

    // Handle version management for each course code
    const finalCourses = [];
    
    Object.keys(coursesByCode).forEach(courseCode => {
      const coursesWithSameCode = coursesByCode[courseCode];
      
      if (coursesWithSameCode.length === 1) {
        const course = coursesWithSameCode[0];
        // For single version, only include if it's published and active
        if (course.publishStatus === 2 && course.activationStatus === 1) {
          finalCourses.push(course);
        }
      } else {
        // Multiple versions exist, apply version management rules
        const resolvedCourse = resolveVersionConflict(coursesWithSameCode);
        if (resolvedCourse) {
          finalCourses.push(resolvedCourse);
        }
      }
    });

    return finalCourses;
  };

  // Resolve version conflicts between courses with the same course code
  const resolveVersionConflict = (coursesWithSameCode) => {
    // Sort by version in descending order (highest version first)
    const sortedCourses = coursesWithSameCode.sort((a, b) => 
      (b.courseVersion || 0) - (a.courseVersion || 0)
    );

    // Find the newest course that is published and active
    const newestActiveCourse = sortedCourses.find(course => 
      course.publishStatus === 2 && course.activationStatus === 1
    );
    
    // Check if user is enrolled in any version of this course
    const enrolledCourse = sortedCourses.find(course => isEnrolledInCourse(course.courseId));
    
    if (enrolledCourse) {
      const enrollment = getCourseEnrollment(enrolledCourse.courseId);
      const assessmentStatus = enrollment?.courseAssessmentStatus;
      
      // If assessment does not start yet (N or null), show the new version
      if (assessmentStatus === "N" || assessmentStatus === null || assessmentStatus === undefined) {
        return newestActiveCourse || null;
      } else {
        // Assessment is completed (P/F/I), show the old version if available
        return enrolledCourse; // Show old version user is working on
      }
    } else {
      // Not enrolled in any version, show the new version
      return newestActiveCourse || null;
    }
  };

  const handleViewDetails = (courseId) => {
    navigate(`/trainingcourses/coursedetails/${courseId}`);
  };

  const getEnrollmentStatus = (courseId) => {
    const enrollment = getCourseEnrollment(courseId);
    if (!enrollment) return null;

    const status = enrollment.courseAssessmentStatus;
    switch (status) {
      case "P":
        return { text: "Passed", class: "status-completed", icon: faCheck };
      case "F":
        return { text: "Failed", class: "status-failed", icon: faCheck };
      case "I":
        return { text: "In Progress", class: "status-progress", icon: faBookOpen };
      case "N":
      case null:
      case undefined:
        return { text: "Enrolled", class: "status-enrolled", icon: faUsers };
      default:
        return { text: "Enrolled", class: "status-enrolled", icon: faUsers };
    }
  };

  const formatCourseDate = (dateString, fallback = "N/A") => {
    if (!dateString) return fallback;
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get all valid courses (filtered by publish/activation status and version management)
  const getValidCourses = () => {
    if (!courses) return [];
    return filterCoursesByStatusAndVersion(courses);
  };

  const validCourses = getValidCourses();

  const isLoading = coursesLoading || enrollmentsLoading;
  const hasError = coursesError || enrollmentsError;

  if (isLoading) {
    return <SubmitLoader />;
  }

  if (hasError) {
    return (
      <AlertMessage
        message={coursesError || enrollmentsError}
        type="error"
        className="training-courses-error"
      />
    );
  }

  return (
    <div className="training-courses-container">
      <div className="training-courses-header">
        <h1>Training Courses</h1>
        <p>Discover and enroll in available training courses to enhance your maritime skills.</p>
      </div>

      {/* Filters and Search */}
      <div className="training-courses-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search courses by name, code, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-container">
          <label htmlFor="status-filter">Filter by Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Courses</option>
            <option value="enrolled">Enrolled Courses</option>
            <option value="available">Available Courses</option>
          </select>
        </div>
      </div>

      {/* Course Statistics */}
      <div className="course-stats">
        <div className="stat-card">
          <FontAwesomeIcon icon={faBookOpen} className="stat-icon" />
          <div className="stat-info">
            <span className="stat-number">{validCourses.length}</span>
            <span className="stat-label">Available Courses</span>
          </div>
        </div>
        <div className="stat-card">
          <FontAwesomeIcon icon={faUsers} className="stat-icon enrolled" />
          <div className="stat-info">
            <span className="stat-number">{userEnrollments.length}</span>
            <span className="stat-label">Enrolled</span>
          </div>
        </div>
        <div className="stat-card">
          <FontAwesomeIcon icon={faCheck} className="stat-icon completed" />
          <div className="stat-info">
            <span className="stat-number">
              {userEnrollments.filter(e => e.courseAssessmentStatus === "P").length}
            </span>
            <span className="stat-label">Completed</span>
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="courses-grid">
        {filteredCourses.length === 0 ? (
          <div className="no-courses">
            <FontAwesomeIcon icon={faBookOpen} className="no-courses-icon" />
            <h3>No courses found</h3>
            <p>Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          filteredCourses.map((course) => {
            const isEnrolled = isEnrolledInCourse(course.courseId);
            const enrollmentStatus = getEnrollmentStatus(course.courseId);
            const isFailed = enrollmentStatus?.class === 'status-failed';
            const coverImageData = getCourseCoverImage(course);

            return (
              <div key={course.courseId} className={`course-card ${isEnrolled ? 'enrolled' : ''} ${isFailed ? 'failed' : ''}`}>
                <div className="course-card-image">
                  <img 
                    src={coverImageData.src}
                    alt={course.courseName}
                    className="course-cover-image"
                    data-placeholder={coverImageData.isPlaceholder}
                    onError={(e) => {
                      e.target.src = noImageAvailable;
                      e.target.setAttribute('data-placeholder', 'true');
                    }}
                  />
                </div>

                <div className="course-card-header">
                  <div className="course-code-version">
                    <span className="course-code">{course.courseCode}</span>
                    {course.courseVersion && (
                      <span className="course-version">v{course.courseVersion}</span>
                    )}
                  </div>
                  {isEnrolled && enrollmentStatus && (
                    <div className={`enrollment-badge ${enrollmentStatus.class}`}>
                      <FontAwesomeIcon icon={enrollmentStatus.icon} />
                      <span>{enrollmentStatus.text}</span>
                    </div>
                  )}
                </div>

                <div className="course-card-body">
                  <h3 className="course-title">{course.courseName}</h3>
                  <p className="course-description">
                    {course.courseDecription || "No description available"}
                  </p>

                  <div className="course-details">
                    {isEnrolled && (
                      <>
                        <div className="course-detail">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>Started: {formatCourseDate(getCourseEnrollment(course.courseId)?.courseStartDate)}</span>
                        </div>
                        <div className="course-detail">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>Complete by: {formatCourseDate(getCourseEnrollment(course.courseId)?.courseCompleteByDate)}</span>
                        </div>
                      </>
                    )}
                    {course.dlTrainingCourseModules && (
                      <div className="course-detail">
                        <FontAwesomeIcon icon={faBookOpen} />
                        <span>{course.dlTrainingCourseModules.length} Module{course.dlTrainingCourseModules.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="course-card-footer">
                  <button
                    className="btn-view-details"
                    onClick={() => handleViewDetails(course.courseId)}
                  >
                    <FontAwesomeIcon icon={faEye} />
                    View Details
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TrainingCourses;